import { EMAIL_SOURCE_LABELS, EMAIL_STATUS_LABELS, type EmailCountsDTO, type EmailListItemDTO } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Inbox, Mail, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge, Button, Card, EmptyState, Input, Skeleton, Tabs, useConfirm, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { useLookups } from '../../hooks/useMeta';
import { api, apiUrl, errorMessage } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { EmailDetailDrawer } from './EmailDetailDrawer';
import { SuppressionsTab } from './SuppressionsTab';
import { TemplatesTab } from './TemplatesTab';

type TabKey = 'outbox' | 'approval' | 'sent' | 'attention' | 'replies' | 'templates' | 'suppressions';

/** Filters behind each list tab (the API is the same list endpoint). */
const TAB_QUERIES: Record<Exclude<TabKey, 'templates' | 'suppressions'>, Record<string, string | boolean>> = {
  outbox: { status: 'scheduled', sort: 'scheduled' },
  approval: { status: 'pending_approval', sort: 'scheduled' },
  sent: { status: 'sent,delivered,bounced', direction: 'outbound' },
  attention: { status: 'failed,uncertain,bounced' },
  replies: { direction: 'inbound' },
};

/** Everything that has gone, will go or came back — plus templates and the do-not-email list. */
export function EmailPage() {
  const [params, setParams] = useSearchParams();
  const { meta } = useLookups();
  const tab = (params.get('tab') as TabKey | null) ?? 'outbox';
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: counts } = useQuery({
    queryKey: ['emails', 'counts'],
    queryFn: ({ signal }) => api.get<EmailCountsDTO>('/api/emails/counts', { signal }),
  });

  return (
    <Page width="wide">
      <PageHeader
        title="Email"
        description="Scheduled, awaiting approval, sent and received — with the safety rails in one place."
        actions={
          <Link to="/email/campaigns">
            <Button icon={Mail}>Bulk sends</Button>
          </Link>
        }
      />

      {meta?.integrations.emailProvider === 'log' && (
        <Card className="mb-4 border-amber-300 bg-amber-50">
          <div className="flex items-start gap-2 p-3 text-[13px] text-amber-900">
            <AlertTriangle className="mt-0.5 size-4" />
            <p>
              <strong>Development email provider.</strong> Emails are recorded here but <strong>not delivered</strong>. Set <code>EMAIL_PROVIDER</code> to
              <code> smtp</code> or <code>postmark</code> to send for real (see the README).
            </p>
          </div>
        </Card>
      )}

      <Card>
        <Tabs
          tabs={[
            { key: 'outbox', label: 'Outbox', count: counts?.outbox },
            { key: 'approval', label: 'Awaiting approval', count: counts?.approval },
            { key: 'sent', label: 'Sent' },
            { key: 'attention', label: 'Needs attention', count: counts?.needsAttention },
            { key: 'replies', label: 'Replies', count: counts?.unmatchedReplies || undefined },
            { key: 'templates', label: 'Templates' },
            { key: 'suppressions', label: 'Do-not-email list' },
          ]}
          value={tab}
          onChange={(key) => setParams({ tab: key }, { replace: true })}
        />
        <div className="p-4">
          {tab === 'templates' ? (
            <TemplatesTab />
          ) : tab === 'suppressions' ? (
            <SuppressionsTab />
          ) : (
            <EmailList tab={tab} onOpen={setOpenId} />
          )}
        </div>
      </Card>

      {openId && <EmailDetailDrawer emailId={openId} onClose={() => setOpenId(null)} />}
    </Page>
  );
}

function EmailList({ tab, onOpen }: { tab: Exclude<TabKey, 'templates' | 'suppressions'>; onOpen: (id: string) => void }) {
  const { can } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const query = { ...TAB_QUERIES[tab], q: q || undefined, pageSize: 100 };
  const { data, isLoading } = useQuery({
    queryKey: ['emails', 'list', query],
    queryFn: ({ signal }) => api.get<{ items: EmailListItemDTO[]; total: number }>(apiUrl('/api/emails', query), { signal }),
  });

  const bulk = useMutation({
    mutationFn: (action: 'approve' | 'reject') => api.post<{ done: string[]; skipped: unknown[] }>(`/api/emails/bulk-${action}`, { ids: selected }),
    onSuccess: async (result, action) => {
      toast.success(`${action === 'approve' ? 'Approved' : 'Rejected'} ${result.done.length} email(s)`);
      setSelected([]);
      await queryClient.invalidateQueries({ queryKey: ['emails'] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const items = data?.items ?? [];

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input className="max-w-xs" placeholder="Search subject, address or text" value={q} onChange={(event) => setQ(event.target.value)} />
        {tab === 'approval' && can('email:approve') && selected.length > 0 && (
          <>
            <Button variant="primary" onClick={() => bulk.mutate('approve')} loading={bulk.isPending}>
              Approve {selected.length}
            </Button>
            <Button
              variant="danger"
              onClick={() =>
                void (async () => {
                  if (await confirm({ title: `Reject ${selected.length} email(s)?`, message: 'They will not be sent.', confirmLabel: 'Reject', danger: true })) bulk.mutate('reject');
                })()
              }
            >
              Reject {selected.length}
            </Button>
          </>
        )}
      </div>
      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={tab === 'replies' ? Inbox : tab === 'attention' ? ShieldOff : Mail}
          title={EMPTY_TITLES[tab]}
          description={EMPTY_DESCRIPTIONS[tab]}
        />
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((email) => (
            <li key={email.id} className="flex flex-wrap items-center gap-2 py-2 text-[13px]">
              {tab === 'approval' && can('email:approve') && (
                <input
                  type="checkbox"
                  aria-label={`Select ${email.subject}`}
                  checked={selected.includes(email.id)}
                  onChange={(event) => setSelected((current) => (event.target.checked ? [...current, email.id] : current.filter((id) => id !== email.id)))}
                />
              )}
              <StatusBadge email={email} />
              <button type="button" onClick={() => onOpen(email.id)} className="min-w-0 truncate font-medium text-slate-900 hover:text-ember-700">
                {email.subject}
              </button>
              <span className="text-slate-500">{email.direction === 'inbound' ? `from ${email.fromEmail}` : `to ${email.toEmail}`}</span>
              {email.businessId ? (
                <Link to={`/leads/${email.businessId}?tab=emails`} className="text-slate-500 hover:text-ember-700">
                  {email.businessName}
                </Link>
              ) : (
                email.direction === 'inbound' && <Badge color="amber" size="sm">not linked to a lead</Badge>
              )}
              {email.source !== 'manual' && email.source !== 'inbound' && <Badge color="violet" size="sm">{EMAIL_SOURCE_LABELS[email.source]}</Badge>}
              {email.failureReason && <span className="w-full truncate text-xs text-red-600">{email.failureReason}</span>}
              <span className="ml-auto text-xs text-slate-400">
                {formatDateTime(email.sentAt ?? email.receivedAt ?? email.scheduledFor ?? email.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const EMPTY_TITLES: Record<Exclude<TabKey, 'templates' | 'suppressions'>, string> = {
  outbox: 'Nothing is waiting to go out',
  approval: 'Nothing to approve',
  sent: 'No emails sent yet',
  attention: 'No problems',
  replies: 'No replies yet',
};

const EMPTY_DESCRIPTIONS: Record<Exclude<TabKey, 'templates' | 'suppressions'>, string> = {
  outbox: 'Scheduled emails and bulk sends held for the sending window appear here.',
  approval: 'Automated follow-ups that need a human OK before they are sent appear here.',
  sent: 'Send an email from a lead’s profile or from the lead table.',
  attention: 'Failed deliveries, bounces and sends whose outcome is unknown appear here for review.',
  replies:
    'Replies are picked up automatically from connected mailboxes that read replies (checked every minute) and from the provider webhook. A reply that can’t be matched to a lead shows up here so you can link it; you can also log a reply on a lead’s profile.',
};

function StatusBadge({ email }: { email: EmailListItemDTO }) {
  if (email.direction === 'inbound') return <Badge color="green" size="sm">reply</Badge>;
  const color =
    email.status === 'sent' || email.status === 'delivered'
      ? 'green'
      : email.status === 'failed' || email.status === 'bounced'
        ? 'red'
        : email.status === 'uncertain'
          ? 'amber'
          : email.status === 'pending_approval'
            ? 'violet'
            : 'slate';
  return (
    <Badge color={color} size="sm">
      {EMAIL_STATUS_LABELS[email.status]}
    </Badge>
  );
}
